import React from 'react';
import qs from 'query-string';
import { withRouter } from 'react-router';
import { Formik } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';

import Form from './Form';
import FieldSet from './FieldSet';
import Input from './Input';
import Textarea from './Textarea';
import PrivacyPolicyCheckbox from './PrivacyPolicyCheckbox';
import { LoadingButton } from '../Buttons';

const StyledInuiryForm = styled.div`
	margin: 0 auto;
	max-width: ${p => p.theme.globalWidthSmall};
`;

const Row = styled.div`
	display: flex;
	margin: 0 -${p => p.theme.gutterXSmall};
`;

const Column = styled.div`
	flex: 1;
	margin: 0 ${p => p.theme.gutterXSmall} 30px ${p => p.theme.gutterXSmall};
`;

yup.setLocale({
	mixed: {
		required: 'Dette feltet er obligatorisk.',
	},
	string: {
		email: 'Du må skrive inn en gyldig e-postadresse.',
		url: 'Du må skrive inn en gyldig nettadresse.',
		max: 'Du kan maks skrive inn ${max} tegn.', // eslint-disable-line no-template-curly-in-string
	},
});

const mapValuesToFields = values => {
	const fields = [
		{ key: 'input_1', value: values.name || null },
		{ key: 'input_2', value: values.regNumber || null },
		{ key: 'input_4', value: values.email || null },
		{ key: 'input_5', value: values.phone || null },
		{ key: 'input_6', value: values.stockNumber || null },
		{ key: 'input_7', value: values.partName || null },
		{ key: 'input_8', value: values.partUrl || null },
		{ key: 'input_9', value: values.comment || null },
		{ key: 'input_10', value: values.address || null },
		{ key: 'input_12', value: values.pnr || null },
	];

	return fields;
};

function InquiryForm({ location, formId, history }) {
	const query = qs.parse(location.search);
	const { id, lagernummer, delenavn } = query;
	const url = id
		? `${window.location.protocol}//${window.location.host}/bildeler/${id}`
		: '';

	return (
		<StyledInuiryForm>
			<Mutation
				mutation={gql`
					mutation submitForm(
						$submission: GFFormSubmission
						$id: Int
					) {
						submission: postGFFormSubmission(
							id: $id
							submission: $submission
						) {
							is_valid
							page_number
							source_page_number
							confirmation_message
						}
					}
				`}>
				{(submit, { loading, error, data }) => (
					<Formik
						initialValues={{
							name: '',
							regNumber: '',
							email: '',
							phone: '',
							stockNumber: lagernummer || '',
							partName: delenavn || '',
							partUrl: url,
							comment: '',
							accepted: false,
						}}
						validationSchema={yup.object().shape({
							name: yup.string().required(),
							regNumber: yup.string(),
							email: yup
								.string()
								.email()
								.required(),
							phone: yup.string().required(),
							stockNumber: yup.string(),
							partName: yup.string(),
							partUrl: yup.string().url(),
							comment: yup
								.string()
								.required()
								.max(500),
							accepted: yup
								.bool()
								.oneOf(
									[true],
									'Du må godkjenne personvernerklæringen.'
								),
						})}
						onSubmit={(values, { setSubmitting }) => {
							submit({
								variables: {
									id: formId,
									submission: {
										fields: mapValuesToFields(values),
									},
								},
							})
								.catch(error => {
									console.error(error);

									setSubmitting(false);
								})
								.then(data => {
									setSubmitting(false);
									history.push('/takk-for-foresporselen/');
								});
						}}>
						{({
							values,
							errors,
							touched,
							handleChange,
							handleBlur,
							handleSubmit,
							isSubmitting,
						}) => (
							<div>
								{data &&
									data.submission && (
										<div
											dangerouslySetInnerHTML={{
												__html:
													data.submission
														.confirmation_message,
											}}
										/>
									)}

								{!(data && data.submission) && (
									<Form onSubmit={handleSubmit}>
										{error && <div>{error.toString()}</div>}
										<FieldSet disabled={isSubmitting}>
											<Row>
												<Column>
													<Input
														name="name"
														label="Navn/Bedrift m/kontaktperson"
														isRequired={true}
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.name}
														error={errors.name}
														touched={touched.name}
													/>
												</Column>

												<Column>
													<Input
														name="regNumber"
														label="Registreringsnummer‎"
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.regNumber}
														error={errors.regNumber}
														touched={
															touched.regNumber
														}
													/>
												</Column>
											</Row>

											<Row>
												<Column>
													<Input
														type="email"
														name="email"
														label="E-post"
														isRequired={true}
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.email}
														error={errors.email}
														touched={touched.email}
													/>
												</Column>

												<Column>
													<Input
														name="phone"
														label="Telefon"
														isRequired={true}
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.phone}
														error={errors.phone}
														touched={touched.phone}
													/>
												</Column>
											</Row>
											<Row>
												<Column>
													<Input
														name="address"
														label="Addresse"
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.address}
														error={errors.address}
														touched={
															touched.address
														}
													/>
												</Column>

												<Column>
													<Input
														name="pnr"
														label="Postnr"
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.pnr}
														error={errors.pnr}
														touched={touched.pnr}
													/>
												</Column>
											</Row>

											<Row>
												<Column>
													<Input
														name="stockNumber"
														label="Lagernummer"
														onChange={handleChange}
														onBlur={handleBlur}
														value={
															values.stockNumber
														}
														error={
															errors.stockNumber
														}
														touched={
															touched.stockNumber
														}
													/>
												</Column>

												<Column>
													<Input
														name="partName"
														label="Bildel"
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.partName}
														error={errors.partName}
														touched={
															touched.partName
														}
													/>
												</Column>
											</Row>

											<Row>
												<Column>
													<Input
														name="partUrl"
														label="Link til bildel"
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.partUrl}
														error={errors.partUrl}
														touched={
															touched.partUrl
														}
													/>
												</Column>
											</Row>

											<Row>
												<Column>
													<Textarea
														name="comment"
														label="Kommentar"
														isRequired={true}
														onChange={handleChange}
														onBlur={handleBlur}
														value={values.comment}
														error={errors.comment}
														touched={
															touched.comment
														}
													/>
												</Column>
											</Row>

											<Row>
												<Column>
													<PrivacyPolicyCheckbox
														name="accepted"
														value={values.accepted}
														checked={
															values.accepted
														}
														error={errors.accepted}
														touched={
															touched.accepted
														}
														onChange={handleChange}
														onBlur={handleBlur}
													/>
												</Column>
											</Row>
										</FieldSet>

										<LoadingButton
											type="submit"
											loading={isSubmitting}>
											Send forespørsel
										</LoadingButton>
									</Form>
								)}
							</div>
						)}
					</Formik>
				)}
			</Mutation>
		</StyledInuiryForm>
	);
}

export default withRouter(InquiryForm);
